import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useSelector } from "react-redux";

import fetchTalentProfile, {
  addToShortlist,
  checkInterviewRequested,
  createInterviewRound,
  fetchInterviewRounds,
  fetchRoles,
  fetchShortlist,
  fetchSkills,
  fetchTalentList,
  fetchTalentPdf,
  fetchTalentFacets,
  fetchTools,
  postPreferences,
  removeFromShortlist,
  requestInterview,
  submitAiSummaryFeedback,
  submitHireRequest,
} from "services/network/talent";
import { ingestAnalyticsEvent } from "services/network/talentAnalytics";
import { hasPermission as portalHasPermission } from "hooks/usePermission";
import { getLoadingAdditionalInfo, getUserAdditionalInfo } from "selectors/user";
import NavigationRoutes from "constants/NavigationRoutes";
import { TALENT_PROFILE_ROUTE } from "constants/routes";

export interface TalentApiUser {
  customerId: string;
  userId: string;
  name: string;
  email: string;
  type: string;
}

export interface TalentApiRoutes {
  home: string;
  talentNetwork: string;
  talentProfile: (id: string) => string;
  requestAHire: string;
  shortlist: string;
  interviewStatus: string;
}

export interface TalentApi {
  kind: "portal" | "plg";
  user: TalentApiUser | null;
  hasPermission: (key: string) => boolean;
  routes: TalentApiRoutes;
  customerState?: string;
  talentNetworkVisible: boolean;
  userInfoLoading: boolean;
  renderAccountMenu?: () => ReactNode;
  fetchTalentList: typeof fetchTalentList;
  fetchTalentProfile: typeof fetchTalentProfile;
  fetchTalentPdf: typeof fetchTalentPdf;
  fetchSkills: typeof fetchSkills;
  fetchTools: typeof fetchTools;
  fetchRoles: typeof fetchRoles;
  fetchTalentFacets: typeof fetchTalentFacets;
  requestInterview: typeof requestInterview;
  checkInterviewRequested: typeof checkInterviewRequested;
  submitHireRequest: typeof submitHireRequest;
  fetchShortlist: typeof fetchShortlist;
  addToShortlist: typeof addToShortlist;
  removeFromShortlist: typeof removeFromShortlist;
  submitAiSummaryFeedback: typeof submitAiSummaryFeedback;
  fetchInterviewRounds: typeof fetchInterviewRounds;
  createInterviewRound: typeof createInterviewRound;
  postPreferences: typeof postPreferences;
  ingestAnalyticsEvent: typeof ingestAnalyticsEvent;
}

const TalentApiContext = createContext<TalentApi | null>(null);

export function TalentApiProvider({ value, children }: { value: TalentApi; children: ReactNode }) {
  return <TalentApiContext.Provider value={value}>{children}</TalentApiContext.Provider>;
}

function readPortalUser(): TalentApiUser | null {
  try {
    const u = JSON.parse(localStorage.getItem("loggedInUser") || "null");
    return u ? { customerId: u.customerId || "", userId: u.userId || "", name: u.name || "", email: u.email || "", type: u.type || "" } : null;
  } catch {
    return null;
  }
}

const PORTAL_ROUTES: TalentApiRoutes = {
  home: NavigationRoutes.dashboard,
  talentNetwork: NavigationRoutes.talentNetwork,
  talentProfile: (id) => TALENT_PROFILE_ROUTE.replace(":talentId", id),
  requestAHire: NavigationRoutes.requestAHire,
  shortlist: NavigationRoutes.shortlist,
  interviewStatus: NavigationRoutes.interviewStatus,
};

/**
 * `useSelector` when a redux Provider is mounted (always, in the app). Components rendered without one
 * (unit tests) get the fallback. Whether a Provider exists never changes between renders, so the hook order is stable.
 */
function useOptionalSelector<T>(select: (state: any) => T, fallback: T): T {
  try {
    return useSelector(select) ?? fallback;
  } catch {
    return fallback;
  }
}

const EMPTY_INFO = {};

function usePortalTalentApi(): TalentApi {
  const info = useOptionalSelector(getUserAdditionalInfo, EMPTY_INFO) || EMPTY_INFO;
  const loading = useOptionalSelector(getLoadingAdditionalInfo, false);
  const { talentNetworkVisibility, state } = info as { talentNetworkVisibility?: boolean; state?: string };
  return useMemo(
    () => ({
      kind: "portal",
      user: readPortalUser(),
      hasPermission: portalHasPermission,
      routes: PORTAL_ROUTES,
      customerState: state,
      talentNetworkVisible: !!talentNetworkVisibility,
      userInfoLoading: !!loading,
      fetchTalentList,
      fetchTalentProfile,
      fetchTalentPdf,
      fetchSkills,
      fetchTools,
      fetchRoles,
      fetchTalentFacets,
      requestInterview,
      checkInterviewRequested,
      submitHireRequest,
      fetchShortlist,
      addToShortlist,
      removeFromShortlist,
      submitAiSummaryFeedback,
      fetchInterviewRounds,
      createInterviewRound,
      postPreferences,
      ingestAnalyticsEvent,
    }),
    [talentNetworkVisibility, state, loading]
  );
}

/** The services, identity and routes the shared talent screens use. Portal by default; the PLG shell provides its own. */
export default function useTalentApi(): TalentApi {
  const override = useContext(TalentApiContext);
  const portal = usePortalTalentApi();
  return override ?? portal;
}
